import { type PaletteKey, type PixelCanvas, sprite } from '../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from './effect-guns.sprite';
import { MYTHIC_GUNS } from './mythic';

/**
 * The mythic sets' guns (ADR-0160): the default gun redrawn whole for each, as an effect skin's is (ADR-0113: the
 * Pollinator's frame, origin, grip and muzzle; a front narrowing to its muzzle; nothing in a skin or hair tone behind
 * the grip). Each has something alive in it, drawn in its frames (a furnace's fire, a lantern's flame, a star turning
 * in its cage, a storm in a jar) and its flair on top at run time (its set's `flair.gun`: a pilot flame, a charm on a cord,
 * beads round the star, arcs between the prongs). No team keys (`b B c`): a held gun isn't recoloured by team.
 */

// --- Hellfire --------------------------------------------------------------------------------------------------------

const OBSIDIAN = ['k', '1', '2', '3'] as const;

const hellfire = (frame: number) =>
  outlined((c) => {
    // The stock: obsidian, cracked over magma.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, ['k', '1', '1', '2'], 1, 2), 'l');
    dots(c, 'o', [
      [8, 20],
      [9, 21],
      [10, 21],
      [11, 20],
      [12, 19],
      [13, 19],
      [14, 20],
      [15, 21],
      [16, 22],
      [17, 22],
      [18, 21],
    ]);
    dots(c, 'y', [
      [10, 21],
      [14, 20],
      [17, 22],
    ]);
    // Two horns over the body.
    for (const [x, lean] of [
      [24, -1],
      [41, 1],
    ] as const)
      for (let k = 0; k < 4; k++)
        c.hline(x + Math.round(lean * k * 0.5), x + Math.round(lean * k * 0.5) + (3 - k), 12 - k, k === 3 ? '3' : '2');
    // The body: obsidian, lit along its top.
    box(c, 22, 13, 45, 26, [2, 1], byRow(13, 26, OBSIDIAN, 1, 3), 'r');
    // The furnace: a grate over fire that climbs and falls.
    box(c, 26, 15, 41, 23, [1], (x, y) => {
      const reach = 2 + ((x * 7 + frame * 3) % 5) + (x % 3 === frame % 3 ? 1 : 0);
      const up = 23 - y;
      if ((x - 26) % 4 === 3) return 'k';
      return up < 1 ? 'w' : up < reach - 3 ? 'y' : up < reach - 1 ? 'Y' : up < reach ? 'o' : up < reach + 2 ? 'R' : 'N';
    });
    c.hline(26, 41, 14, '2');
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? '3' : '2'), 'r');
    // The barrel: obsidian narrowing to its mouth, three vents glowing in turn.
    taper(c, 46, 58, 19.5, 5, 2.4, round(OBSIDIAN));
    for (const [i, x] of [48, 51, 54].entries()) {
      const hot = (i + frame) % 3 === 0;
      c.vline(x, 18, 20, hot ? 'y' : 'o');
      c.set(x, 21, hot ? 'Y' : 'u');
    }
    // Its mouth: a ring of hot metal, white at the very tip.
    taper(c, 59, 61, 19.5, 2.6, 2.2, (top, bottom) => (top === 0 ? 'y' : bottom === 0 ? 'u' : 'Y'));
    dots(c, frame % 2 ? 'w' : 'y', [
      [61, 19],
      [61, 20],
    ]);
  });

// --- Foxfire ---------------------------------------------------------------------------------------------------------

const LACQUER = ['4', '5', 'w', 'w'] as const;
const GOLD = ['U', 'U', 'Y', 'y'] as const;

const foxfire = (frame: number) =>
  outlined((c) => {
    // The stock: red lacquer, a gold cap, a white binding.
    box(c, 5, 16, 22, 24, [2, 1], byRow(16, 24, ['N', 'R', 'r', 'i'], 1, 2), 'l');
    box(c, 5, 17, 7, 23, [1], (_x, y) => (y < 19 ? 'y' : y > 21 ? 'U' : 'Y'), 'l');
    c.vline(13, 17, 23, 'w').vline(14, 17, 23, '5');
    // The body: white lacquer, a gold line along its top, a red three-comma crest on its side.
    box(c, 22, 14, 45, 25, [2, 1], byRow(14, 25, LACQUER, 2, 2), 'r');
    c.hline(23, 44, 14, 'Y');
    dots(c, 'r', [
      [36, 17],
      [37, 17],
      [38, 18],
      [38, 19],
      [35, 19],
      [35, 20],
      [36, 21],
      [37, 21],
      [39, 20],
      [40, 20],
      [40, 21],
      [39, 22],
    ]);
    dots(c, 'R', [
      [37, 19],
      [37, 20],
    ]);
    // A shrine lantern on its back: a red roof on two posts over a gold foot, a flame between them.
    box(c, 25, 4, 39, 5, [1], (_x, y) => (y === 4 ? 'i' : 'r'));
    c.hline(27, 37, 6, 'R');
    c.vline(27, 7, 12, 'N').vline(37, 7, 12, 'N');
    c.hline(26, 38, 13, 'Y').hline(27, 37, 12, 'U');
    const flame = [
      [32, 11, 'w'],
      [31, 11, 'i'],
      [33, 11, 'i'],
      [32, 10, 'w'],
      [31, 10, 'i'],
      [33, 10, 'p'],
      [32, 9, 'i'],
      [frame % 2 ? 31 : 33, 9, 'p'],
      [frame % 2 ? 32 : 31, 8, 'p'],
    ] as const;
    for (const [x, y, k] of flame) c.set(x, y, k);
    // The trigger, gold.
    box(c, 31, 26, 34, 28, [0, 1], (_x, y) => (y === 26 ? 'Y' : 'U'), 'r');
    // The barrel: white, bound in red, a gold muzzle.
    taper(c, 46, 58, 19.5, 3.4, 2.1, round(LACQUER));
    for (const x of [47, 48, 54])
      taper(c, x, x, 19.5, 3.4 - (x - 46) * 0.1, 3.4 - (x - 46) * 0.1, round(['N', 'R', 'r', 'i']));
    taper(c, 59, 61, 19.5, 2.4, 2, round(GOLD));
  });

// --- Singularity -----------------------------------------------------------------------------------------------------

const VOID = ['k', 'k', '1', 'p'] as const;

const singularity = (frame: number) =>
  outlined((c) => {
    // The stock and the body: the night's black, a violet edge where the light bends, stars on it.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, ['k', 'k', '1', 'P'], 1, 2), 'l');
    c.hline(8, 20, 20, 'P');
    box(c, 22, 14, 45, 26, [2, 1], byRow(14, 26, VOID, 1, 3), 'r');
    dots(c, 'w', [
      [26, 18],
      [35, 22],
      [41, 17],
      [12, 22],
    ]);
    dots(c, 'p', [
      [30, 21],
      [38, 19],
      [17, 18],
    ]);
    // Two claws rising off the body, holding a black star between them: a violet rim, a ring round it, a bead of
    // light running round the ring.
    for (const [x, d] of [
      [25, 1],
      [39, -1],
    ] as const) {
      c.vline(x, 9, 13, '2')
        .vline(x + d, 6, 9, '2')
        .set(x + d * 2, 5, '3')
        .set(x, 9, '3');
    }
    for (let y = 5; y <= 14; y++)
      for (let x = 27; x <= 37; x++) {
        const r = Math.hypot(x + 0.5 - 32.5, y + 0.5 - 9.5);
        if (r <= 4.6) c.set(x, y, r > 3.6 ? (x + y < 41 ? 'i' : 'P') : 'k');
      }
    for (let x = 24; x <= 41; x++) {
      const t = (x - 32.5) / 8.5;
      const y = Math.round(10.2 + Math.sqrt(Math.max(0, 1 - t * t)) * 2.4 - t * 1.2);
      c.set(x, y, 'p');
    }
    const bead = [25, 29, 34, 39][frame % 4] as number;
    const t = (bead - 32.5) / 8.5;
    const by = Math.round(10.2 + Math.sqrt(Math.max(0, 1 - t * t)) * 2.4 - t * 1.2);
    c.set(bead, by, 'w').set(bead - 1, by, 'i');
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? 'P' : '1'), 'r');
    // Two rails closing on the muzzle, the dark between them, motes of light running down it.
    for (let x = 46; x <= 60; x++) {
      const k = (x - 46) / 14;
      const top = Math.round(15 + k * 2);
      const bottom = Math.round(24 - k * 2);
      c.set(x, top, 'p')
        .set(x, top + 1, '1')
        .set(x, bottom, 'k')
        .set(x, bottom - 1, '1');
      if (x <= 48) c.vline(x, top, bottom, x === 46 ? 'P' : '1');
      else if ((x + frame * 3) % 6 === 0) c.set(x, 19, 'i').set(x - 1, 19, 'p');
    }
    dots(c, frame % 2 ? 'w' : 'i', [
      [61, 19],
      [61, 20],
    ]);
  });

// --- Thunderclap -----------------------------------------------------------------------------------------------------

const STEEL = ['1', '2', '3', '4'] as const;
/** The storm in the jar per frame: a bolt's pixels (none: the cloud is dark). */
const STORMS: readonly (readonly (readonly [number, number])[])[] = [
  [
    [30, 8],
    [31, 9],
    [30, 10],
    [31, 11],
    [32, 12],
  ],
  [],
  [
    [35, 7],
    [34, 8],
    [35, 9],
    [34, 10],
    [33, 11],
    [34, 12],
  ],
  [],
];

const thunderclap = (frame: number) =>
  outlined((c) => {
    // The stock: storm steel, a gold cap, gold rivets.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, STEEL, 1, 2), 'l');
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? 'y' : y > 22 ? 'U' : 'Y'), 'l');
    dots(c, 'y', [
      [11, 19],
      [16, 19],
      [20, 19],
    ]);
    // A storm in a jar on its back: glass over a dark cloud, a bolt in it now and then.
    for (let y = 3; y <= 13; y++)
      for (let x = 24; x <= 40; x++) {
        const r = ((x + 0.5 - 32.5) / 8) ** 2 + ((y + 0.5 - 12) / 9) ** 2;
        if (r > 1) continue;
        const cloud =
          Math.hypot(x + 0.5 - 29.5, y + 0.5 - 8.5) < 3.4 ||
          Math.hypot(x + 0.5 - 34.5, y + 0.5 - 7.8) < 3.8 ||
          (y > 8 && y < 12);
        c.set(x, y, r > 0.74 ? (x + y < 38 ? 'w' : '5') : cloud ? (y < 7 ? '3' : y < 10 ? '2' : '1') : '4');
      }
    for (const [x, y] of STORMS[frame % 4] ?? []) c.set(x, y, 'y');
    if ((STORMS[frame % 4] ?? []).length) c.set(32, 6, 'w');
    // The body: steel edged in gold, a gold bolt on its side.
    box(c, 22, 13, 45, 26, [2, 1], byRow(13, 26, STEEL, 2, 3), 'r');
    c.hline(23, 44, 13, 'Y').hline(24, 43, 14, 'y');
    dots(c, 'y', [
      [36, 16],
      [35, 17],
      [34, 18],
      [35, 19],
      [36, 19],
      [35, 20],
      [34, 21],
      [33, 22],
    ]);
    dots(c, 'Y', [
      [37, 16],
      [36, 17],
      [37, 19],
      [36, 20],
      [35, 21],
    ]);
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? 'Y' : 'U'), 'r');
    // The coil: a steel core wound with gold.
    taper(c, 46, 56, 19.5, 3.6, 2.6, round(STEEL));
    for (const x of [48, 51, 54])
      taper(c, x, x, 19.5, 4.4 - (x - 46) * 0.12, 4.4 - (x - 46) * 0.12, round(GOLD as readonly PaletteKey[]));
    // Two gold prongs closing on the muzzle, the air between them alive.
    for (let x = 56; x <= 62; x++) {
      const k = (x - 56) / 6;
      const top = Math.round(15 + k * 2.4);
      const bottom = Math.round(24 - k * 2.4);
      c.set(x, top, 'y')
        .set(x, top + 1, 'Y')
        .set(x, bottom, 'U')
        .set(x, bottom - 1, 'Y');
    }
    if (frame % 2 === 0)
      dots(c, frame === 0 ? 'w' : 'y', [
        [60, 19],
        [59, 20],
        [60, 21],
      ]);
  });

const idle = (gun: (frame: number) => (c: PixelCanvas) => void, fps: number) => ({
  idle: { fps, frames: [gun(0), gun(1), gun(2), gun(3)] },
});

export default [
  sprite({
    name: 'pollinator_inferno',
    ...geometry,
    notes: `Hellfire (ADR-0160, the Inferno set's gun): obsidian cracked over magma, two horns over the body, a furnace in its
side (a grate over fire that climbs and falls across 4 frames), a barrel narrowing to a ring of hot metal, three vents
glowing in turn. Its flair: a pilot flame standing at the muzzle, the furnace's light, embers. Rubric (4×): S4 R5 P4 L4
C4 A4 F4. Weakest: the horns are 4 px, spikes more than horns at 1:1; the magma crack on the stock is behind the face.`,
    anims: idle(hellfire, 9),
  }),
  sprite({
    name: 'pollinator_kitsune',
    ...geometry,
    notes: `Foxfire (ADR-0160, the Kitsune set's gun): white lacquer and red, gold fittings, a red three-comma crest on its
side, a shrine lantern on its back (a red roof on two posts over a gold foot) with a flame in it, a white barrel bound
in red to a gold muzzle. Its flair: the lantern's flame in the team's light, a paper charm swinging on a red cord under
the stock. Rubric (4×): S4 R5 P5 L4 C4 A4 F4. Weakest: the crest is a dozen px, a red knot at 1:1.`,
    anims: idle(foxfire, 6),
  }),
  sprite({
    name: 'pollinator_voidwalker',
    ...geometry,
    notes: `Singularity (ADR-0160, the Voidwalker set's gun): the night's black with a violet edge and stars on it, two claws
holding a black star over the body (a violet rim, a ring round it, a bead of light running round the ring over 4
frames), two rails closing on the muzzle with the dark between them, motes of light running down it. Its flair: beads
circling the star, its light breathing. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: black on the night's dark ground
leans on its violet edges.`,
    anims: idle(singularity, 8),
  }),
  sprite({
    name: 'pollinator_tempest',
    ...geometry,
    notes: `Thunderclap (ADR-0160, the Tempest set's gun): storm steel edged in gold with a gold bolt on its side, a storm in a
jar on its back (glass over a dark cloud, a bolt in it on two frames of four), a steel coil wound with gold, two gold
prongs closing on the muzzle, a spark between them. Its flair: arcs jumping between the prongs, their light. Rubric (4×):
S4 R5 P4 L4 C4 A4 F4. Weakest: the jar's cloud is 3 tones in 9 px, a dark blob at 1:1 until its bolt lights.`,
    anims: idle(thunderclap, 7),
  }),
  // The mythic sets after the first four (ADR-0160 amended): a file a set in `mythic/`.
  ...MYTHIC_GUNS,
];
